import Image from 'next/image';
import { useSelector, useDispatch } from 'react-redux';
import { Button, RadioButton } from '@BICE_Vida/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, FORMATERS, Modal, ModalBody, ModalContent } from '@BICE_Vida/scp-components';
import { CAT, IMG_GREETING_CAT_DIALOGUE, QUOTER_COVERAGES } from '@/utils/assets';
import styles from './Quoter.module.scss';
import Select from '@/designSystem/Select';
import { getBasePlans, getPlanByType } from '@/utils/estimationFunctions';
import * as plansSelectors from '../../redux/plans/selectors';
import * as wizardSelector from '../../redux/wizard/selectors';
import * as wizardActions from '../../redux/wizard/actions';
import * as ufSelectors from '../../redux/uf/selectors';

import { AvailablePlan, Variant as VariantType } from '@/src/types/availablePlans';
import IncrementDecrement from '../IncrementDecrement';
import CardSelectable from '../CardSelectable';
import { coveragesStepTexts, quoterStepStexts } from '@/utils/generalTexts';
import AvailablePlanModal from '../AvailablePlanModal';
import { PayloadVariant } from '@/src/types/variants';
import PymeDataForm from './PymeDataForm';

import componentsId from '@/utils/componentsId';
import Variants from './Variant';
import WizardStepper from '../Wizard/WizardStepper';
import { formatUF } from '@/utils/format';
import { CoverageResume } from '@/src/types/coverages';
import { TypeCoverage, TypeVariant } from '@/utils/constants';
import {
  quotationFlowEvents,
  trackPageViewQuotation,
  clickActionsEvents,
} from '@/utils/dataLayer/quotationFlow';

// Preplan: Planes base que se muestran al inicio del wizard (premium, elite, elite plus, standard)
// PlanSelected: Plan seleccionado + las coverturas (eg: 'premium + dental + catastrófico')

function Quoter() {
  const dispatch = useDispatch();

  const UF = useSelector(ufSelectors.getUF);

  const [isSelected, setIsSelected] = useState(false);

  const workers = useSelector(wizardSelector.getWorkersSelected);
  const filteredPlansByType = useSelector(plansSelectors.getPlansByType());
  const selectedPlanId = useSelector(wizardSelector.getPlanSelected);
  const coverageSelected = useSelector(wizardSelector.getSelectedCoverages);
  const variantSelected = useSelector(wizardSelector.getVariantSelected);

  const prePlanSelectedId = useSelector(wizardSelector.getPrePlanSelected);
  const getDataLayerInfo = useSelector(wizardSelector.getDataLayerInfo);
  const branding = useSelector(wizardSelector.getBranding);

  const dentalBasePlan = useSelector(plansSelectors.getPlanByCoverage(TypeCoverage.dental));
  const catastrophicBasePlan = useSelector(
    plansSelectors.getPlanByCoverage(TypeCoverage.catastrophic)
  );
  const deceaseBasePlan = useSelector(plansSelectors.getPlanByCoverage(TypeCoverage.decease));
  const planSelected = useSelector(plansSelectors.getPlanById(selectedPlanId));
  const variants: VariantType[] = useSelector(plansSelectors.getVariantsByPlanId(selectedPlanId));
  const availablePlans: AvailablePlan[] = useSelector(plansSelectors.getAvailablePlans);

  const [coveragesModalDetails, setCoveragesModalDetails] = useState<{
    show: boolean;
    plan: {
      base: AvailablePlan;
      resume: CoverageResume;
      theme: 'primary' | 'secondary';
    } | null;
  }>({
    show: false,
    plan: null,
  });
  const [showVariantsModal, setShowVariantsModal] = useState(false);
  const [showDistribution, setShowDistribution] = useState(
    variantSelected.distributed ? TypeVariant.withCharges : TypeVariant.withoutCharges
  );

  const prePlanSelected = availablePlans.find((plan) => plan._id === prePlanSelectedId);

  const plansByType = getBasePlans({ plans: availablePlans });

  const basePlan = prePlanSelected && {
    plan: prePlanSelected,
    cost: prePlanSelected.price.uf,
    coverage: {
      health: prePlanSelected.coverage.health,
      life: prePlanSelected.coverage.life,
      decease: prePlanSelected.coverage.decease,
    },
  };

  const dentalPlan = prePlanSelected
    ? getPlanByType(availablePlans, prePlanSelected, TypeCoverage.dental)
    : undefined;

  const catastrophicPlan = prePlanSelected
    ? getPlanByType(availablePlans, prePlanSelected, TypeCoverage.catastrophic)
    : undefined;

  const deceasePlan = prePlanSelected
    ? getPlanByType(availablePlans, prePlanSelected, TypeCoverage.decease)
    : undefined;

  const distribution = useMemo(
    () =>
      variants
        .filter((variant) => !!variant.active && variant.description !== TypeVariant.withoutCharges)
        .map((variant) => {
          const variantSave = variantSelected.selected.find(
            (selected) => selected.descriptionReview === variant.description
          );

          const workers =
            (!!variantSave && variantSelected.distributed && variantSave.workers) || 0;
          const uf =
            (!!variantSave && variantSelected.distributed && variant.price?.uf * workers) || 0;

          return {
            ...variant,
            input: {
              feedback: variantSave?.input?.feedback ?? '',
              hasError: variantSave?.input?.hasError ?? false,
              workers,
              uf,
            },
          };
        }),
    [variants, variantSelected]
  );

  const counterWorkers = useMemo(() => {
    return distribution.reduce((acc, variant) => acc + (variant.input?.workers || 0), 0);
  }, [distribution]);

  const setPlanSelected = useCallback(
    (description: string) => {
      const foundPlan = plansByType.find((plan) => plan.description === description);

      if (foundPlan) {
        dispatch(wizardActions.setPrePlanSelected(foundPlan._id));
        dispatch(wizardActions.setPlanSelected(foundPlan._id));
        dispatch(wizardActions.setPrePlanType(foundPlan.type));

        if (coverageSelected.dental && !foundPlan.coverage.dental) {
          dispatch(wizardActions.setDentalCoverage(true));
        }
        if (coverageSelected.catastrophic && !foundPlan.coverage.catastrophic) {
          dispatch(wizardActions.setCatastrophicCoverage(true));
        }
        if (coverageSelected.decease && !foundPlan.coverage.decease) {
          dispatch(wizardActions.setDeceaseCoverage(true));
        }
      }
    },
    [dispatch, plansByType, coverageSelected]
  );

  const getBasePlan = useCallback(
    (
      type: 'dental' | 'catastrophic' | 'decease' | 'default' = 'default'
    ): {
      base: AvailablePlan;
      resume: CoverageResume;
      theme: 'primary' | 'secondary';
    } | null => {
      const basePlans = {
        [TypeCoverage.dental]: dentalBasePlan
          ? {
              base: {
                ...dentalBasePlan?.plan,
                description: dentalBasePlan?.plan?.description.replace('+', '') ?? '',
                price: { uf: dentalBasePlan?.cost || 0 },
              },
              resume: dentalBasePlan,
              theme: 'primary' as const,
            }
          : null,
        [TypeCoverage.catastrophic]: catastrophicBasePlan
          ? {
              base: {
                ...catastrophicBasePlan?.plan,
                description: catastrophicBasePlan?.plan?.description.replace('+', '') ?? '',
                price: { uf: catastrophicBasePlan?.cost || 0 },
              },
              resume: catastrophicBasePlan,
              theme: 'primary' as const,
            }
          : null,
        [TypeCoverage.decease]: deceaseBasePlan
          ? {
              base: {
                ...deceaseBasePlan?.plan,
                description: deceaseBasePlan?.plan?.description.replace('+', '') ?? '',
                price: { uf: deceaseBasePlan?.cost || 0 },
              },
              resume: deceaseBasePlan,
              theme: 'primary' as const,
            }
          : null,
        default: basePlan
          ? {
              base: {
                ...basePlan?.plan,
                description: basePlan?.plan.description.replace('+', '') ?? '',
                price: { uf: basePlan?.cost || 0 },
              },
              resume: basePlan,
              theme: 'secondary' as const,
            }
          : null,
      };

      return basePlans[type];
    },
    [dentalBasePlan, catastrophicBasePlan, deceaseBasePlan, basePlan]
  );

  const setShowCoveragesDetail = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, type: keyof typeof TypeCoverage) => {
      event.stopPropagation();
      const plan = getBasePlan(type);

      setCoveragesModalDetails({
        show: true,
        plan,
      });
    },
    [getBasePlan, setCoveragesModalDetails]
  );

  const setWorkersSelected = (workers: number) => dispatch(wizardActions.setWorkers(workers));
  const setWorkers = async (workers: number, action?: 'increment' | 'decrement') => {
    const actionMap = {
      decrement: clickActionsEvents.decrementWorkers,
      increment: clickActionsEvents.incrementWorkers,
    };

    if (action && actionMap[action]) {
      trackPageViewQuotation({
        eventParams: {
          ...quotationFlowEvents.quoterStep,
          ...getDataLayerInfo.event_params,
          action_type: 'click',
          action: actionMap[action],
        },
        user: getDataLayerInfo.user,
      });
    }

    setWorkersSelected(workers);
  };
  const setCorrespondingPlan = (id: string) => dispatch(wizardActions.setPlanSelected(id));

  const setCoverage = useCallback(
    (coverage: string) => {
      const coverages = {
        [TypeCoverage.dental]: () =>
          dispatch(wizardActions.setDentalCoverage(!coverageSelected.dental)),
        [TypeCoverage.catastrophic]: () =>
          dispatch(wizardActions.setCatastrophicCoverage(!coverageSelected.catastrophic)),
        [TypeCoverage.decease]: () =>
          dispatch(wizardActions.setDeceaseCoverage(!coverageSelected.decease)),
      };

      coverages[coverage as keyof typeof TypeCoverage]();
    },
    [coverageSelected, dispatch]
  );

  const setVariantDistribution = useCallback(
    (id: string, newDistribution?: Array<VariantType>) => {
      const payload = () => {
        const commonData: PayloadVariant = {
          distributed: false,
          variantId: id,
          selected: [],
        };

        if (id !== componentsId.variantsStep.distributionVariant) {
          const variant = variants.find((variant: VariantType) => variant._id === id);

          if (!variant) return commonData;

          return {
            ...commonData,
            selected: [
              {
                variantId: id,
                descriptionReview: variant.description,
                descriptionSummary: variant.texts.distribution.summary,
                showValue: variant.texts.distribution.showValue,
                workers,
                totalVariant: {
                  uf: parseFloat((workers * variant.price.uf).toFixed(2)),
                },
              },
            ],
          };
        }
        const variantsDistributed = newDistribution?.map((variant) => ({
          variantId: variant._id,
          descriptionReview: variant.description,
          descriptionSummary: variant.texts.distribution.summary,
          showValue: variant.texts.distribution.showValue,
          workers: variant.input?.workers || 0,
          totalVariant: {
            uf: variant.input?.uf || 0,
          },
          input: variant.input ?? undefined,
        }));

        return {
          ...commonData,
          distributed: true,
          selected: variantsDistributed || [],
        };
      };

      dispatch(wizardActions.setVariantSelected(payload()));
    },
    [dispatch, variants, workers]
  );

  const saveDistribution = (newDistribution: Array<VariantType>) => {
    setVariantDistribution(componentsId.variantsStep.distributionVariant, newDistribution);
  };

  const dentalFilter = filteredPlansByType.filter((plan: AvailablePlan) => {
    const hasDental = coverageSelected.dental
      ? plan.coverage.dental !== null
      : plan.coverage.dental === null;
    const hasDecease = coverageSelected.decease
      ? plan.coverage.decease !== null
      : plan.coverage.decease === null;

    return hasDental && hasDecease;
  });

  const correspondingPlan = dentalFilter.find((plan: AvailablePlan) =>
    coverageSelected.catastrophic
      ? plan.coverage.catastrophic !== null
      : plan.coverage.catastrophic === null
  );

  const saveVariantSelected = useCallback(
    (distributed: boolean, id: string) => {
      if (distributed) {
        setShowDistribution(TypeVariant.withCharges);
        setVariantDistribution(componentsId.variantsStep.distributionVariant);
      } else {
        setVariantDistribution(id);
        setShowDistribution(TypeVariant.withoutCharges);
      }
    },
    [setShowDistribution, setVariantDistribution]
  );

  useEffect(() => {
    if (
      variantSelected.distributed &&
      variantSelected.selected.length === 0 &&
      showDistribution === TypeVariant.withCharges
    ) {
      setVariantDistribution(componentsId.variantsStep.distributionVariant, distribution);
    }
  }, [distribution, variantSelected]);

  useEffect(() => {
    if (showDistribution === TypeVariant.withoutCharges) {
      const variantWithoutCharges = variants.find(
        (variant: VariantType) => variant.description === TypeVariant.withoutCharges
      );

      if (variantWithoutCharges?._id) setVariantDistribution(variantWithoutCharges?._id);
    }
  }, [workers, variants]);

  useEffect(() => {
    if (
      variants.length > 0 &&
      variantSelected.distributed &&
      showDistribution === TypeVariant.withCharges
    ) {
      setVariantDistribution(componentsId.variantsStep.distributionVariant, distribution);
    }
  }, [variants]);

  useEffect(() => {
    !!correspondingPlan &&
      correspondingPlan._id !== selectedPlanId &&
      setCorrespondingPlan(correspondingPlan._id);
  }, [coverageSelected]);

  useEffect(() => {
    trackPageViewQuotation({
      eventParams: {
        ...quotationFlowEvents.quoterStep,
        ...getDataLayerInfo.event_params,
        attribution: branding.code,
      },
      user: getDataLayerInfo.user,
    });
  }, []);

  const coveragesArray = useMemo(
    () =>
      [
        {
          id: TypeCoverage.dental,
          icon: QUOTER_COVERAGES.ICON_COVERAGES_TOOTH,
          title: coveragesStepTexts.dental.title,
          description: `Desde ${FORMATERS.formatCLP(
            (dentalPlan?.cost || 0) * UF.value
          )} / ${formatUF(dentalPlan?.cost || 0, UF.value)} mensual por trabajador`,
          value: 'dental',
          checked: coverageSelected.dental || false,
          total: `${FORMATERS.formatCLP(
            (dentalPlan?.cost || 0) * UF.value * (workers || 0)
          )} / ${formatUF((dentalPlan?.cost || 0) * (workers || 0))}`,
          active: !!dentalPlan?.plan,
        },
        {
          id: TypeCoverage.catastrophic,
          icon: QUOTER_COVERAGES.ICON_COVERAGES_CRUTCHES,
          title: coveragesStepTexts.catastrophic.title,
          description: `Desde ${FORMATERS.formatCLP(
            (catastrophicPlan?.cost || 0) * UF.value
          )}/ ${formatUF(catastrophicPlan?.cost || 0, UF.value)} mensual por trabajador`,
          value: 'catastrophic',
          checked: coverageSelected.catastrophic || false,
          total: `${FORMATERS.formatCLP(
            (catastrophicPlan?.cost || 0) * UF.value * (workers || 0)
          )} / ${formatUF((catastrophicPlan?.cost || 0) * (workers || 0))}`,
          active: !!catastrophicPlan?.plan,
        },
        {
          id: TypeCoverage.decease,
          icon: QUOTER_COVERAGES.ICON_COVERAGES_MONITOR,
          title: coveragesStepTexts.decease.title,
          description: `${FORMATERS.formatCLP((deceasePlan?.cost || 0) * UF.value)}/ ${formatUF(
            deceasePlan?.cost || 0,
            UF.value
          )} mensual por trabajador`,
          value: 'decease',
          checked: coverageSelected.decease || false,
          total: `${FORMATERS.formatCLP(
            (deceasePlan?.cost || 0) * UF.value * (workers || 0)
          )} / ${formatUF((deceasePlan?.cost || 0) * (workers || 0))}`,
          badge: 'Solo titulares',
          active: !!deceasePlan?.plan,
        },
      ].filter((coverage) => coverage.active),
    [catastrophicPlan, dentalPlan, deceasePlan, coverageSelected, UF.value, workers]
  );

  const handleCloseCoveragesModal = () => {
    setCoveragesModalDetails({ show: false, plan: null });
    document.documentElement.style.overflow = 'auto';
  };

  const coverageActionMap = {
    catastrophic: clickActionsEvents.quoterStepCatastrophicModal,
    dental: clickActionsEvents.quoterStepDentalModal,
    decease: clickActionsEvents.quoterStepDeceaseModal,
  };

  return (
    <div className={styles.quoter_container}>
      <>
        {coveragesModalDetails.plan && coveragesModalDetails.show && (
          <AvailablePlanModal
            isOpen={coveragesModalDetails.show}
            handleClose={handleCloseCoveragesModal}
            isSelectedCoverage={isSelected}
            skipAssistances={isSelected}
            plan={coveragesModalDetails.plan.base}
            coverages={coveragesModalDetails.plan.resume.coverage}
            UF={UF}
            theme={coveragesModalDetails.plan.theme}
          />
        )}
      </>
      <Modal
        isOpen={showVariantsModal}
        onClose={() => setShowVariantsModal(false)}
        closeOnOverlay
        classNameModal={styles.quoter_variant_modal}
      >
        <ModalBody
          img={CAT.CAT_THINKING_ROUND}
          className={styles.quoter_variant_modal_body}
          content={
            <ModalContent
              title={quoterStepStexts.variantsModal.title}
              className={styles.quoter_variant_text_modal_content}
            >
              <div className={styles.preexisting_conditions_text_modal_body}>
                {quoterStepStexts.variantsModal.content}
              </div>
            </ModalContent>
          }
        />
        <div className={styles.quoter_variant_container_buttons}>
          <Button
            text="Entendido"
            variant="primary"
            size="large"
            onClick={() => setShowVariantsModal(false)}
            className={styles.button_text}
          />
        </div>
      </Modal>

      <div className={styles.quoter_container_header}>
        <Image src={IMG_GREETING_CAT_DIALOGUE} height={80} width={93} alt="gato saludando" />
        <h1 className={styles.quoter_container_header_title}>{quoterStepStexts.title}</h1>
        <h3 className={styles.quoter_container_header_description}>{quoterStepStexts.subtitle}</h3>
        <WizardStepper />
      </div>
      <div className={styles.quoter_container_content}>
        <div className={styles.quoter_container_content_left}>
          <div>
            <div className={styles.quoter_container_content_title}>
              {quoterStepStexts.PlansTitle}
            </div>
            <Select
              customClass={styles.quoter_select}
              id="select_plan"
              options={plansByType.map((plan) => plan.description)}
              value={prePlanSelected?.description || ''}
              onChange={(value) => setPlanSelected(value)}
            />
          </div>
          <div>
            <div className={styles.quoter_container_content_title}>
              {quoterStepStexts.workersTitle}
            </div>
            <div className={styles.quoter_container_content_workers}>
              <IncrementDecrement
                handleChange={setWorkers}
                quantity={workers}
                minValue={6}
                hasError={workers < 5}
              />
              <div className={styles.quoter_container_content_workers_info}>
                <div>{workers || 0} trabajadores</div>
                <div>{`${FORMATERS.formatCLP(
                  (planSelected?.price?.uf || 0) * (workers || 0) * UF.value
                )} / ${formatUF((planSelected?.price?.uf || 0) * (workers || 0))}`}</div>
              </div>
            </div>
          </div>
          <div>
            <div className={styles.quoter_container_content_title}>
              {quoterStepStexts.coveragesTitle}
            </div>
            <div>{quoterStepStexts.coveragesSubtitle}</div>
            <div className={styles.quoter_container_content_coverages}>
              {coveragesArray.map((coverage) => (
                <CardSelectable
                  id={`quoter_plan_${coverage.title}`}
                  image={coverage.icon}
                  key={`quoter_plan_${coverage.title}`}
                  title={coverage.title}
                  description={coverage.description}
                  onClick={() => setCoverage(coverage.value)}
                  className={styles.quoter_card}
                  selector="check"
                  checked={coverage.checked}
                  action={
                    <Button
                      id={`quoter_plan_${coverage.title}_button`}
                      text="Ver coberturas"
                      variant="text"
                      size="medium"
                      onClick={(e) => {
                        const action =
                          coverageActionMap[coverage.id as keyof typeof coverageActionMap];

                        setShowCoveragesDetail(e, coverage.id);
                        setIsSelected(true);

                        trackPageViewQuotation({
                          eventParams: {
                            ...quotationFlowEvents.quoterStep,
                            ...getDataLayerInfo.event_params,
                            action_type: 'click',
                            action,
                          },
                          user: getDataLayerInfo.user,
                        });
                      }}
                      className={styles.card_selectable_button}
                    />
                  }
                  badge={coverage.badge}
                />
              ))}
            </div>
          </div>
          <div>
            <div className={styles.quoter_variant_container}>
              <div>
                <div className={styles.quoter_container_content_title}>
                  {quoterStepStexts.variantsTitle}
                </div>
                <div>{quoterStepStexts.variantsSubtitle}</div>
              </div>
              <Button
                variant="text"
                text={quoterStepStexts.variantsModalButton}
                size="medium"
                onClick={() => setShowVariantsModal(true)}
                className={styles.card_selectable_button}
              />
            </div>
            <div className={styles.quoter_container_content_variants}>
              {variants
                .filter((variant: VariantType) => !!variant.visible)
                .map((variant: VariantType, idx: number) => (
                  <div key={`${variant._id}-${idx}`} className={styles.radio_button_container}>
                    <RadioButton
                      onChange={() =>
                        saveVariantSelected(
                          variant.description === TypeVariant.withCharges,
                          variant._id
                        )
                      }
                      name={variant.description}
                      checked={showDistribution === variant.description}
                    />
                    {variant.description}
                  </div>
                ))}
            </div>
          </div>
          {showDistribution === TypeVariant.withCharges && (
            <>
              <Variants
                totalWorkers={workers}
                UF={UF}
                setDistribution={saveDistribution}
                distribution={distribution}
                counterWorkers={counterWorkers}
              />
              <Alert
                text={quoterStepStexts.variantsAlert}
                customClass={styles.quoter_container_content_variants_alert}
                iconName="info"
              />
            </>
          )}
        </div>

        <div className={styles.quoter_container_content_right_container}>
          <PymeDataForm />
        </div>
      </div>
    </div>
  );
}

export default Quoter;
